import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { hvApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Lock, Loader2, ArrowLeft, Zap, CreditCard, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import HVNav from '../components/HVNav';
import HVFooter from '../components/HVFooter';

const PLANS = [
  {
    key: 'free',
    name: 'Kostenlos',
    price: 0,
    description: 'Einstieg — für den ersten Bewertungslink',
    color: 'border-border',
    features: [
      'Bewertungs-Funnel',
      '1 Bewertungslink',
      'Basis-Dashboard',
    ],
    locked: [
      'Unbegrenzte Links',
      'KI-Textvorschläge',
      'Logo & Markenfarben',
      'CSV-Export',
      'KI-Sachlichkeitsindex',
    ],
    cta: 'Aktueller Plan',
    disabled: true,
  },
  {
    key: 'standard',
    name: 'Standard',
    price: 29,
    description: 'Für aktive Hausverwaltungen',
    color: 'border-blue-400',
    badge: null,
    features: [
      'Alles aus Kostenlos',
      'Unbegrenzte Links',
      'KI-Textvorschläge',
      'Logo & Markenfarben',
      'Gruppen-Segmentierung',
    ],
    locked: [
      'CSV-Export & Statistiken',
      'KI-Sachlichkeitsindex',
      'MetaScore',
    ],
    cta: 'Auf Standard upgraden',
  },
  {
    key: 'premium',
    name: 'Premium',
    price: 79,
    description: 'Für professionelle Verwaltungen',
    color: 'border-violet-400',
    badge: 'Beliebt',
    features: [
      'Alles aus Standard',
      'CSV-Export & Funnel-Statistiken',
      'KI-Sachlichkeitsindex',
      'MetaScore plattformübergreifend',
    ],
    locked: [
      'Custom Domain',
      'Custom Design',
      'Automatische Antworten',
    ],
    cta: 'Auf Premium upgraden',
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    price: 199,
    description: 'Für große Portfolios & Gruppen',
    color: 'border-amber-400',
    badge: null,
    features: [
      'Alles aus Premium',
      'Custom Domain',
      'Custom Design',
      'Automatische Antworten',
      'Dedizierter Ansprechpartner',
    ],
    locked: [],
    cta: 'Enterprise anfragen',
    enterprise: true,
  },
];

const PLAN_RANK = { free: 0, mvp: 0, standard: 1, premium: 2, enterprise: 3 };

export default function HVBilling() {
  const { user, loading: authLoading } = useAuth('hv_user');
  const [searchParams] = useSearchParams();
  const [loadingPlan, setLoadingPlan] = useState(null);

  const success = searchParams.get('success');
  const canceled = searchParams.get('canceled');
  const successPlan = searchParams.get('plan');

  const rawPlan = user?.tenant?.billing?.plan || 'free';
  const currentRank = PLAN_RANK[rawPlan] ?? 0;

  const handleUpgrade = async (planKey) => {
    if (planKey === 'enterprise') {
      window.location.href = 'mailto:af@dwayne-advertising.de?subject=Enterprise-Anfrage';
      return;
    }
    setLoadingPlan(planKey);
    try {
      const res = await hvApi.createCheckoutSession(planKey);
      window.location.href = res.data.url;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Fehler beim Starten des Checkouts';
      toast.error(msg);
    } finally {
      setLoadingPlan(null);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <HVNav />

      <main className="container-main py-8">
        {/* Back + Header */}
        <div className="mb-8">
          <Link to="/app" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Übersicht
          </Link>
          <h1 className="text-2xl font-semibold mb-1">Abonnement & Upgrade</h1>
          <p className="text-muted-foreground">
            Aktueller Plan: <span className="font-medium text-foreground capitalize">{rawPlan === 'mvp' ? 'Kostenlos' : rawPlan}</span>
          </p>
        </div>

        {/* Success / Cancel banners */}
        {success && (
          <div className="mb-6 flex items-center gap-3 p-4 rounded-lg bg-green-50 border border-green-200 text-green-800">
            <CheckCircle className="w-5 h-5 shrink-0 text-green-600" />
            <div>
              <p className="font-semibold">Upgrade erfolgreich!</p>
              <p className="text-sm">
                {successPlan
                  ? `Ihr Plan wird in Kürze auf ${successPlan.charAt(0).toUpperCase() + successPlan.slice(1)} aktualisiert.`
                  : 'Ihr Plan wird in Kürze aktualisiert.'}
                {' '}Vielen Dank!
              </p>
            </div>
          </div>
        )}
        {canceled && (
          <div className="mb-6 flex items-center gap-3 p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800">
            <AlertCircle className="w-5 h-5 shrink-0 text-yellow-600" />
            <p className="text-sm">Checkout abgebrochen. Kein Betrag wurde belastet.</p>
          </div>
        )}

        {/* SEPA info */}
        <div className="mb-8 flex items-center gap-3 p-3 rounded-lg bg-muted/60 border border-border text-sm text-muted-foreground">
          <CreditCard className="w-5 h-5 shrink-0" />
          <span>
            Zahlung per <strong className="text-foreground">SEPA-Lastschrift</strong> — automatischer Einzug monatlich.
            Kündigung jederzeit zum Monatsende möglich. Abwicklung über Stripe.
          </span>
        </div>

        {/* Plans grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLANS.map((plan) => {
            const isCurrent = rawPlan === plan.key || (plan.key === 'free' && (rawPlan === 'free' || rawPlan === 'mvp'));
            const isLowerThanCurrent = PLAN_RANK[plan.key] < currentRank;
            const isHigher = PLAN_RANK[plan.key] > currentRank;

            return (
              <Card
                key={plan.key}
                className={`card-shadow flex flex-col border-2 transition-all ${plan.color} ${isCurrent ? 'ring-2 ring-primary/30 bg-primary/5' : ''}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <CardTitle className="text-base">{plan.name}</CardTitle>
                    <div className="flex gap-1.5 shrink-0">
                      {isCurrent && (
                        <Badge className="text-[10px] px-1.5 py-0 bg-primary/15 text-primary border border-primary/30 font-semibold">
                          Aktuell
                        </Badge>
                      )}
                      {plan.badge && !isCurrent && (
                        <Badge className="text-[10px] px-1.5 py-0 bg-violet-100 text-violet-700 border border-violet-300 font-semibold">
                          {plan.badge}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold">€{plan.price}</span>
                    {plan.price > 0 && <span className="text-sm text-muted-foreground">/Monat</span>}
                    {plan.price === 0 && <span className="text-sm text-muted-foreground">kostenlos</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{plan.description}</p>
                </CardHeader>

                <CardContent className="flex flex-col flex-1 pt-2">
                  <ul className="space-y-1.5 flex-1 mb-4">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                    {plan.locked.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground/50">
                        <Lock className="w-3.5 h-3.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {isCurrent ? (
                    <Button variant="outline" disabled className="w-full">
                      Aktueller Plan
                    </Button>
                  ) : isLowerThanCurrent ? (
                    <Button variant="ghost" disabled className="w-full text-muted-foreground">
                      Downgrade nicht möglich
                    </Button>
                  ) : plan.enterprise ? (
                    <Button
                      variant="outline"
                      className="w-full border-amber-400 text-amber-700 hover:bg-amber-50"
                      onClick={() => handleUpgrade(plan.key)}
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      {plan.cta}
                    </Button>
                  ) : (
                    <Button
                      className="w-full"
                      onClick={() => handleUpgrade(plan.key)}
                      disabled={loadingPlan === plan.key}
                    >
                      {loadingPlan === plan.key
                        ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        : <Zap className="w-4 h-4 mr-2" />}
                      {plan.cta}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ / fine print */}
        <div className="mt-10 max-w-2xl">
          <h2 className="text-base font-semibold mb-4">Häufige Fragen</h2>
          <div className="space-y-4 text-sm text-muted-foreground">
            <div>
              <p className="font-medium text-foreground mb-1">Wie funktioniert die SEPA-Lastschrift?</p>
              <p>Sie erteilen beim Checkout einmalig ein SEPA-Mandat. Der monatliche Betrag wird automatisch von Ihrem Konto eingezogen — kein manueller Aufwand.</p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Kann ich jederzeit kündigen?</p>
              <p>Ja. Die Kündigung ist jederzeit zum Ende des laufenden Abrechnungsmonats möglich. Danach wechseln Sie automatisch auf den Kostenlos-Plan.</p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Was passiert mit meinen Daten beim Downgrade?</p>
              <p>Ihre Bewertungen und Links bleiben erhalten. Funktionen, die Ihr neuer Plan nicht enthält, werden ausgeblendet bis Sie wieder upgraden.</p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Gibt es eine Testphase?</p>
              <p>Neue Accounts starten mit einem kostenlosen Testzugang im Premium-Umfang für 14 Tage — danach wechseln Sie auf Ihren gewählten Plan.</p>
            </div>
          </div>
        </div>
      <HVFooter />
      </main>
    </div>
  );
}
